import { useQueries } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useDebounce } from 'use-debounce';

import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { VulnerabilityAdvancedFilters } from '@/sections/vulnerabilities/types/filter.types';
import { RiskGroup } from '@/sections/vulnerabilities/types/grouping.types';
import { useAuth } from '@/state/auth';
import {
  GraphQuery,
  QueryFilter,
  Risk,
  RiskPriority,
  RiskSeverity,
} from '@/types';
import { buildAttackSurfaceFilter } from '@/utils/attackSurface.util';

/**
 * Fields used for grouping risks in the grouped view.
 * Must match AllowedColumns in backend/pkg/query/allowed_columns.go
 *
 * Note: Risk nodes store status as a 2-char code (e.g., "OH" = Open+High)
 * which encodes both the lifecycle status and severity. There is no separate
 * "severity" property on Risk nodes — use "status" for grouping.
 */
const RISK_GROUP_BY_FIELDS = ['name', 'status'] as const;

/**
 * Converts VulnerabilityAdvancedFilters to GraphQuery filters for Risk nodes
 * Only includes filters that apply to Risk node properties directly
 * (excludes relationship-based filters like kev, exploit, cvss, epss, sso)
 */
function convertToNodeFilters(
  advancedFilters?: VulnerabilityAdvancedFilters
): QueryFilter[] {
  if (!advancedFilters) return [];

  const nodeFilters: QueryFilter[] = [];

  // Status filter (STARTS WITH to match backend behavior)
  if (advancedFilters.status && advancedFilters.status.length > 0) {
    nodeFilters.push({
      field: 'status',
      operator: 'STARTS WITH',
      value: advancedFilters.status,
    });
  }

  // Severity filter (priority field with RiskPriority mapping)
  if (advancedFilters.severity && advancedFilters.severity.length > 0) {
    nodeFilters.push({
      field: 'priority',
      operator: '=',
      value: advancedFilters.severity.map(
        severity => RiskPriority[severity as RiskSeverity]
      ),
    });
  }

  // Origin/source filter (manual vs non-manual logic from useRisks.ts lines 456-488)
  // Note: filter handler stores values under 'origin' key (not 'source')
  if (advancedFilters.origin && advancedFilters.origin.length > 0) {
    const hasManual = advancedFilters.origin.includes('@praetorian.com');
    const nonManualOrigins = advancedFilters.origin.filter(
      origin => origin !== '@praetorian.com'
    );
    const hasNonManual = nonManualOrigins.length > 0;

    const hasManualFilter: QueryFilter = {
      field: 'source',
      operator: 'ENDS WITH',
      value: '@praetorian.com',
    };

    const hasNonManualOriginsFilter: QueryFilter = {
      field: 'origins',
      operator: 'IN',
      value: nonManualOrigins,
      reverseOperands: true,
    };

    if (hasManual && hasNonManual) {
      nodeFilters.push({
        field: '',
        operator: 'OR',
        value: [hasNonManualOriginsFilter, hasManualFilter],
      });
    } else if (hasManual) {
      nodeFilters.push(hasManualFilter);
    } else if (hasNonManual) {
      nodeFilters.push(hasNonManualOriginsFilter);
    }
  }

  // Tags filter
  if (advancedFilters.tags && advancedFilters.tags.length > 0) {
    nodeFilters.push({
      field: 'tags',
      operator: 'IN',
      value: advancedFilters.tags,
      not: false,
      reverseOperands: true,
    });
  }

  // Attack surface filter (denormalized boolean fields on Risk nodes since ENG-1862)
  if (
    advancedFilters.attackSurface &&
    advancedFilters.attackSurface.length > 0
  ) {
    const surfaceFilter = buildAttackSurfaceFilter(
      advancedFilters.attackSurface
    );
    if (surfaceFilter) nodeFilters.push(surfaceFilter);
  }

  // Date range filter
  if (advancedFilters.dateRange) {
    const { field, startDate, endDate } = advancedFilters.dateRange;

    // Start date filter (>=)
    nodeFilters.push({
      field,
      operator: '>=',
      value: `${startDate}T00:00:00Z`,
    });

    // End date filter (<=)
    nodeFilters.push({
      field,
      operator: '<=',
      value: `${endDate}T23:59:59Z`,
    });
  }

  return nodeFilters;
}

/**
 * Hook for fetching and managing grouped risks
 * Uses GraphQuery with groupBy to get aggregated risk groups,
 * then fetches children on-demand when groups are expanded
 */
export const useGroupedRisks = (
  enabled: boolean,
  advancedFilters?: VulnerabilityAdvancedFilters,
  search?: string,
  doNotImpersonate?: boolean,
  email?: string
) => {
  const axios = useAxios({ doNotImpersonate, email });
  const { friend } = useAuth();

  // Debounce search input (same pattern as useRisks.ts)
  const [debouncedSearch] = useDebounce(search || '', search === '' ? 0 : 300);

  // Track which groups are expanded
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Convert advancedFilters to node filters
  const nodeFilters = useMemo(
    () => convertToNodeFilters(advancedFilters),
    [advancedFilters]
  );

  // Build query with groupBy and aggregations
  const groupQuery: GraphQuery = useMemo(
    () => ({
      node: {
        labels: ['Risk'],
        filters: nodeFilters,
        // Fulltext search — covers key, name, dns, title via riskSearchIndex
        ...(debouncedSearch && debouncedSearch.length >= 2
          ? { search: debouncedSearch }
          : {}),
      },
      groupBy: [...RISK_GROUP_BY_FIELDS],
      aggregations: [
        { function: 'min', field: 'created' },
        { function: 'max', field: 'visited' },
        { function: 'max', field: 'title' },
      ],
      limit: 25,
    }),
    [nodeFilters, debouncedSearch]
  );

  // Fetch grouped data
  const isEnabled = enabled;

  const {
    data: groupsData,
    isLoading: isLoadingGroups,
    isFetchingNextPage,
    error: groupsError,
    fetchNextPage,
    hasNextPage,
  } = useMy(
    {
      resource: 'risk',
      query: groupQuery,
    },
    {
      enabled: isEnabled,
      doNotImpersonate,
      email,
    }
  );

  // Type assertion: when groupBy is used, data contains RiskGroup[]
  const groups = (groupsData || []) as unknown as RiskGroup[];

  // Generate deterministic group key
  const getGroupKey = useCallback((group: RiskGroup) => {
    return `${group.fields.name}::${group.fields.status}`;
  }, []);

  // Build child queries for each expanded group using useQueries
  // useGetUserKey is called here at the top level of useGroupedRisks (not inside map callbacks)
  // to provide the user-identifying suffix for impersonation-aware cache isolation.
  // For per-query keys, we replicate the same logic inline since hooks can't be called in callbacks.
  const childQueries = useQueries({
    queries: groups
      .filter(group => expandedGroups.has(getGroupKey(group)))
      .map(group => {
        const groupKey = getGroupKey(group);
        const childQuery: GraphQuery = {
          node: {
            labels: ['Risk'],
            filters: [
              ...nodeFilters,
              { field: 'name', operator: '=', value: group.fields.name },
              { field: 'status', operator: '=', value: group.fields.status },
            ],
            // Fulltext search — same as group query
            ...(debouncedSearch && debouncedSearch.length >= 2
              ? { search: debouncedSearch }
              : {}),
          },
          limit: 100,
        };

        // Replicate useGetUserKey logic inline (hooks cannot be called in map callbacks)
        const baseKey = getQueryKey.getMy('risk', JSON.stringify(childQuery));
        const queryKey =
          !doNotImpersonate && friend
            ? [...baseKey, friend]
            : email
              ? [...baseKey, email]
              : baseKey;

        return {
          queryKey,
          queryFn: async () => {
            const { data } = await axios({
              method: 'POST',
              url: '/my',
              params: {
                label: 'risk',
              },
              data: childQuery,
            });
            return ((data as Record<string, unknown>).risks || []) as Risk[];
          },
          enabled: expandedGroups.has(groupKey),
        };
      }),
  });

  // Surface per-group child query errors
  useEffect(() => {
    childQueries.forEach(query => {
      if (query.error) {
        toast.error(`Failed to load grouped risks: ${query.error.message}`);
      }
    });
  }, [childQueries.map(q => q.error).join(',')]);

  // Build child map from query results
  const childrenByKey = useMemo(() => {
    const map = new Map<string, Risk[]>();
    const expandedGroupsList = groups.filter(group =>
      expandedGroups.has(getGroupKey(group))
    );

    expandedGroupsList.forEach((group, index) => {
      const groupKey = getGroupKey(group);
      const queryResult = childQueries[index];
      if (queryResult?.data) {
        map.set(groupKey, queryResult.data);
      }
    });

    return map;
  }, [groups, childQueries, expandedGroups, getGroupKey]);

  // Track loading state per group
  const isChildrenLoading = useMemo(() => {
    return childQueries.some(query => query.isLoading);
  }, [childQueries]);

  // Derive loadingGroups from childQueries (no state needed)
  const loadingGroups = useMemo(() => {
    const loading = new Set<string>();
    const expandedGroupsList = groups.filter(group =>
      expandedGroups.has(getGroupKey(group))
    );

    expandedGroupsList.forEach((group, index) => {
      const groupKey = getGroupKey(group);
      const queryResult = childQueries[index];
      if (queryResult?.isLoading) {
        loading.add(groupKey);
      }
    });
    return loading;
  }, [groups, childQueries, expandedGroups, getGroupKey]);

  // Toggle group expansion
  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  return {
    groups,
    isLoading: isLoadingGroups,
    error: groupsError,
    expandedGroups,
    toggleGroup,
    childrenByKey,
    isChildrenLoading,
    loadingGroups, // T013: Expose loading groups state
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
